import { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

const QuestionnaireModal = ({ isOpen, onClose, onComplete }) => {
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const questions = [
    {
      id: 'travelStyle',
      question: 'What\'s your preferred travel style?',
      options: [
        { value: 'solo', label: 'Solo retreats' },
        { value: 'group', label: 'Group experiences' },
        { value: 'couple', label: 'Couple getaways' },
        { value: 'flexible', label: 'I\'m flexible' },
      ],
    },
    {
      id: 'wellnessFocus',
      question: 'What wellness focus interests you most?',
      options: [
        { value: 'mental-health', label: 'Mental health & mindfulness' },
        { value: 'fitness', label: 'Fitness & movement' },
        { value: 'digital-detox', label: 'Digital detox' },
        { value: 'spiritual', label: 'Spiritual growth' },
        { value: 'all', label: 'A bit of everything' },
      ],
    },
    {
      id: 'duration',
      question: 'How long do you prefer your retreats?',
      options: [
        { value: 'weekend', label: 'Weekend (2-3 days)' },
        { value: 'week', label: 'A week (5-7 days)' },
        { value: 'extended', label: 'Extended (10+ days)' },
        { value: 'flexible', label: 'I\'m flexible' },
      ],
    },
    {
      id: 'budget',
      question: 'What\'s your typical budget range?',
      options: [
        { value: 'budget', label: 'Budget-friendly ($500-$1,000)' },
        { value: 'moderate', label: 'Moderate ($1,000-$2,500)' },
        { value: 'premium', label: 'Premium ($2,500+)' },
        { value: 'flexible', label: 'I\'m flexible' },
      ],
    },
    {
      id: 'destination',
      question: 'What type of destination appeals to you?',
      options: [
        { value: 'mountains', label: 'Mountains & nature' },
        { value: 'coastal', label: 'Coastal & beach' },
        { value: 'desert', label: 'Desert & remote' },
        { value: 'tropical', label: 'Tropical & warm' },
        { value: 'any', label: 'Any destination' },
      ],
    },
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    
    // Auto-advance to next question
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    } else {
      // Last question, submit
      handleSubmit({ ...answers, [questionId]: value });
    }
  };

  const handleSubmit = async (finalAnswers) => {
    if (!user) return;
    
    setSubmitting(true);
    try {
      const result = await api.saveQuestionnaire(user.uid, finalAnswers);
      if (result.error) {
        console.error('Error saving questionnaire:', result.error);
      } else {
        // Update user document to mark questionnaire as completed
        await api.updateUser(user.uid, { questionnaireCompleted: true });
        onComplete();
      }
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit(answers);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  if (!isOpen) return null;

  const currentQ = questions[currentQuestion];
  const hasAnswer = answers[currentQ.id];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900">Help us personalize your experience</h2>
            <p className="text-sm text-gray-600 mt-1">
              Question {currentQuestion + 1} of {questions.length}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-emerald-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Question Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">
            {currentQ.question}
          </h3>
          
          <div className="space-y-3">
            {currentQ.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQ.id, option.value)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  answers[currentQ.id] === option.value
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                    : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50 text-gray-700'
                }`}
              >
                <span className="font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentQuestion === 0}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Back
          </button>
          
          {currentQuestion < questions.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={!hasAnswer}
              className="px-6 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              onClick={() => handleSubmit(answers)}
              disabled={!hasAnswer || submitting}
              className="px-6 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Saving...' : 'Complete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireModal;

